import API from 'api';
import {
  SET_EDITING_ACTIVITY,
  CHANGE_ACTIVITY_DATA,
  SET_DELETED_PROBLEMS,
  EDITING_ACTIVITY_SAVED,
  ADD_CONTENT_FILES_TO_DELETE,
  RESTORE_CONTENT_FILES,
} from 'actions/types';
import { uploading } from '../statusActions';
import { getActivityData } from '../gameActions';
import { setActivitiesUnsavedChanges } from './editList';
import { changeSettingsForReal } from './editList';
import { withLoading } from 'utils';
import { activitiesKey } from 'config';
import { prepareActivity } from 'activity-templates/utils';
import ActivityTemplates from 'activity-templates';


const FLC_SAVE_DEBUG_STYLE = 'color:#8a6d00;background:#fff3cd;font-weight:bold;padding:2px 6px;border-radius:3px;';

const isEbookDebugEnabledFromConfig = (config = {}) => (
  config?.customContentSettings?.developerTools?.ebookDebug === true
);

const cleanSaveDebugText = value => String(value || '')
  .replace(/<br\s*\/?\s*>/gi, ' ')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&#160;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getSaveProblemSample = problem => {
  const textItems = Array.isArray(problem?.text) ? problem.text : [];
  return textItems
    .map(item => cleanSaveDebugText(item?.raw || item?.text || item?.value || ''))
    .filter(Boolean)
    .join(' | ')
    .slice(0, 220);
};

const getSaveDataSummary = data => {
  const languageVersionData = data?.languageVersionData || {};
  return {
    gameDataFirstPage: getSaveProblemSample(data?.gameData?.problems?.[0] || {}),
    languageVersionKeys: Object.keys(languageVersionData),
    languageVersions: Object.keys(languageVersionData).reduce((acc, key) => {
      const versionData = languageVersionData[key] || {};
      acc[key] = {
        problemCount: Array.isArray(versionData.problems) ? versionData.problems.length : 0,
        firstPage: getSaveProblemSample(versionData.problems?.[0] || {}),
      };
      return acc;
    }, {}),
  };
};

const flcSaveDebug = (enabled, label, payload = {}) => {
  if (!enabled || typeof console === 'undefined' || !console.log) return;
  console.log(`%c[FLC SAVE DEBUG] ${label}`, FLC_SAVE_DEBUG_STYLE, payload);
  console.warn('[FLC SAVE DEBUG]', label, payload);
};


const EBOOK_TRANSLATION_SNAPSHOT_PREFIX = 'flcMake.ebookPageTranslationSnapshot.';

const cloneData = value => JSON.parse(JSON.stringify(value || {}));

const applyPendingEbookTranslationSnapshots = (activityId, data, debugEnabled = false) => {
  if (typeof window === 'undefined' || !window.sessionStorage || !activityId || !data) {
    return { data, appliedSnapshots: [] };
  }

  const prefix = `${EBOOK_TRANSLATION_SNAPSHOT_PREFIX}${activityId}.`;
  const keys = [];

  for (let index = 0; index < window.sessionStorage.length; index += 1) {
    const key = window.sessionStorage.key(index);
    if (key && key.indexOf(prefix) === 0) keys.push(key);
  }

  if (!keys.length) return { data, appliedSnapshots: [] };

  const nextData = cloneData(data);
  nextData.languageVersionData = nextData.languageVersionData || {};
  const appliedSnapshots = [];

  keys.forEach((key) => {
    try {
      const snapshot = JSON.parse(window.sessionStorage.getItem(key) || '{}');
      const { versionType, problemIndex, problem } = snapshot;
      const index = Number(problemIndex);

      if (!versionType || !Number.isInteger(index) || index < 0 || !problem) return;

      nextData.languageVersionData[versionType] = nextData.languageVersionData[versionType] || {};
      const versionData = nextData.languageVersionData[versionType];
      versionData.problems = Array.isArray(versionData.problems) ? [...versionData.problems] : [];

      // Translation snapshots may restore translated page content, but current
      // editor media is authoritative. Image/video/audio changes are written
      // directly to Redux before save, so an older snapshot must not overwrite
      // either a newly selected file or an explicit media deletion.
      const currentProblem = cloneData(versionData.problems[index] || {});
      const snapshotProblem = cloneData(problem);
      const snapshotMedia = {
        image: snapshotProblem.image || '',
        video: snapshotProblem.video || '',
        audio: snapshotProblem.audio || '',
      };

      // Translation snapshots contain translated page content only. Media belongs
      // to the live editor state and must never be injected from a translation
      // response (especially when the localized page has no media field yet).
      ['image', 'video', 'audio'].forEach((mediaKey) => {
        delete snapshotProblem[mediaKey];
      });

      versionData.problems[index] = {
        ...currentProblem,
        ...snapshotProblem,
      };

      appliedSnapshots.push({
        key,
        versionType,
        problemIndex: index,
        sample: getSaveProblemSample(problem),
        currentMedia: {
          image: currentProblem.image || '',
          video: currentProblem.video || '',
          audio: currentProblem.audio || '',
        },
        snapshotMedia,
        resultMedia: {
          image: versionData.problems[index].image || '',
          video: versionData.problems[index].video || '',
          audio: versionData.problems[index].audio || '',
        },
      });
    } catch (error) {
      flcSaveDebug(debugEnabled, 'saveEditingActivity: failed to apply eBook translation snapshot', {
        key,
        error: error?.message || error,
      });
    }
  });

  return {
    data: appliedSnapshots.length ? nextData : data,
    appliedSnapshots,
  };
};

const clearAppliedEbookTranslationSnapshots = (keys = [], debugEnabled = false) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  keys.forEach((key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      flcSaveDebug(debugEnabled, 'saveEditingActivity: failed to clear eBook translation snapshot', {
        key,
        error: error?.message || error,
      });
    }
  });
};

const filter = 'flinkMake/activity';

export const setEditingActivity = (id, activityTemplates, activeActivity = null) => (dispatch) =>
  withLoading(dispatch, async () => {
    if (!id) {
      return dispatch({
        filter,
        type: SET_EDITING_ACTIVITY,
        payload: null,
      });
    }

    try {
      const activity = await getActivityData(id, activityTemplates, activeActivity);

      if (!activity) {
        throw new Error('Failed to get Activity Data');
      }

      const preparedActivity = prepareActivity(activity);

      dispatch({
        filter,
        type: SET_EDITING_ACTIVITY,
        payload: preparedActivity,
      });

      return preparedActivity;
    } catch (err) {
      console.log(err);
      return false;
    }
  });

export const saveEditingActivity = () => (dispatch, getState) =>
  withLoading(dispatch, async () => {
    const state = getState();
    const {
      editingActivity,
      activityWasChanged,
      filesToDelete,
    } = state.flinkMake.activity;
    const ebookDebugEnabled = isEbookDebugEnabledFromConfig(state.common?.config);

    if (!editingActivity || !editingActivity.activity || !editingActivity.data || !editingActivity.template) {
      flcSaveDebug(ebookDebugEnabled, 'saveEditingActivity: skipped because editingActivity is not available', {
        hasEditingActivity: !!editingActivity,
        activityWasChanged,
      });
      return true;
    }

    const { activity } = editingActivity;
    const snapshotMerge = applyPendingEbookTranslationSnapshots(activity?._id, editingActivity.data, ebookDebugEnabled);

    if (!activityWasChanged && !snapshotMerge.appliedSnapshots.length) {
      return true;
    }

    const { alias } = editingActivity.template;
    const TemplateData = ActivityTemplates[alias];

    if (!TemplateData || !TemplateData.validateActivity) {
      throw new Error('Missing validate activity function');
    }

    const isValid = TemplateData.validateActivity(editingActivity.data);

    const data = snapshotMerge.data;

    flcSaveDebug(ebookDebugEnabled, 'saveEditingActivity: payload summary before API save', {
      activityId: activity?._id,
      templateAlias: alias,
      activityWasChanged,
      appliedSnapshots: snapshotMerge.appliedSnapshots,
      dataSummary: getSaveDataSummary(data),
    });
    
    let versionType = '';
    if (activity.currentLanguageVersion && activity.currentLanguageVersion.version 
      && activity.currentLanguageVersion.locale ) {
      versionType = activity.currentLanguageVersion.version+'_'+activity.currentLanguageVersion.locale;
    }
    
    // Save activity data to S3
    return API.activity
      .saveActivityData(activity._id, data)
      .then(async (res) => {
        if (res === false) {
          throw new Error('Activity data save returned false');
        }

        flcSaveDebug(ebookDebugEnabled, 'saveEditingActivity: API save success', {
          activityId: activity?._id,
          templateAlias: alias,
          appliedSnapshots: snapshotMerge.appliedSnapshots,
          dataSummary: getSaveDataSummary(data),
        });

        clearAppliedEbookTranslationSnapshots(snapshotMerge.appliedSnapshots.map(item => item.key), ebookDebugEnabled);

        // If activity isValid was changed, then save it to DB
        if (activity.isValid !== isValid) {
          activity.isValid = isValid;

          await changeSettingsForReal([activity])(dispatch);
        }

        // Remove content files
        if (filesToDelete.length) {
          const path = `${activitiesKey}/${activity._id}/gamedata`;
          const keys = filesToDelete.map((f) => `${path}/${f}`);
console.log('filesToDelete gamedata-path: ', [path,filesToDelete])
          await API.storage.deleteFiles(keys);
        }

        if (filesToDelete.length && versionType) {
          const path = `${activitiesKey}/${activity._id}/languageVersionData/${versionType}`;
          const keys = filesToDelete.map((f) => `${path}/${f}`);
console.log('filesToDelete languageVersionData-path: ', [path,filesToDelete])
          await API.storage.deleteFiles(keys);
        }

        dispatch({
          filter,
          type: EDITING_ACTIVITY_SAVED,
        });

        dispatch(setActivitiesUnsavedChanges(false));

        return true;
      })
      .catch((err) => {
        alert('Error while saving data. Try again please');
        flcSaveDebug(ebookDebugEnabled, 'saveEditingActivity: API save failed', {
          activityId: activity?._id,
          templateAlias: alias,
          error: err?.message || err,
          dataSummary: getSaveDataSummary(data),
        });
        console.log(err);
        return false;
      });
  });


export const syncGeneratedAudioReferences = ({
  activityId = '',
  targetVersionType = '',
  isOriginalEnglish = false,
  savedFiles = [],
} = {}) => async (dispatch, getState) => {
  const state = getState();
  let editingActivity = state.flinkMake?.activity?.editingActivity;
  // Leaving the content editor clears this shared Redux slot asynchronously.
  // Reload the persisted activity instead of regenerating audio or saving a stale
  // full editor payload. Never patch a different activity after selection changes.
  const activeActivity = state.flinkMake?.editList?.activeActivity;
  if (activityId && activeActivity?._id !== activityId) return false;
  if (!editingActivity?.data && activityId) {
    const loaded = await getActivityData(activityId, state.common?.activityTemplates || [], activeActivity);
    if (!loaded || getState().flinkMake?.editList?.activeActivity?._id !== activityId) return false;
    // Another editor load may have completed while the persisted data was read.
    editingActivity = getState().flinkMake?.activity?.editingActivity;
    if (!editingActivity?.data) {
      editingActivity = prepareActivity(loaded);
      dispatch({ filter, type: SET_EDITING_ACTIVITY, payload: editingActivity });
    }
  }
  if (activityId && (getState().flinkMake?.editList?.activeActivity?._id !== activityId
    || editingActivity?.activity?._id !== activityId)) return false;

  if (!editingActivity?.data || !Array.isArray(savedFiles) || !savedFiles.length) {
    return false;
  }

  if (!isOriginalEnglish && !targetVersionType) {
    return false;
  }

  const rootPath = isOriginalEnglish
    ? 'gameData'
    : `languageVersionData.${targetVersionType}`;
  let applied = 0;

  savedFiles.forEach((item) => {
    const audioPath = String(item?.dataPath || item?.audioPath || item?.fieldPath || '').trim();
    const filename = String(item?.filename || '').trim();

    if (!audioPath || !filename) return;

    dispatch({
      filter,
      type: CHANGE_ACTIVITY_DATA,
      payload: {
        propPath: `${rootPath}.${audioPath}`,
        val: filename,
      },
    });
    applied += 1;
  });

  if (!applied) return false;

  // The backend has already persisted these exact references. Mark the local copy
  // as synchronized without sending the complete, potentially stale editor payload
  // back to data.json.
  dispatch({
    filter,
    type: EDITING_ACTIVITY_SAVED,
  });
  dispatch(setActivitiesUnsavedChanges(false));

  return true;
};

export const uploadAudioFile = (params) => async (dispatch) => {
  const { file, filename, path, fileToDelete, useUploadBucket } = params;

  const key = `${path}/${filename}`;

  const success = await API.storage.uploadFile(key, file, {
    onUploadProgress: (progress) => dispatch(uploading(progress)),
  }, useUploadBucket);

  dispatch(uploading(null));

  if (!success) {
    return false;
  }

  if (fileToDelete) {
    const key = `${path}/${fileToDelete}`;
    await API.storage.deleteFiles([key]);
  }

  return true;

  // return Storage.put(key, file, {
  //   contentType: 'audio/mpeg',
  //   progressCallback: (progress) => dispatch(uploading(progress)),
  // })
  // .then((result) => {
  //   dispatch(uploading(null));
  //   console.log('upload result', result);

  //   return fileToDelete
  //     ? Storage.remove(path + '/' + fileToDelete).then((result) =>
  //         console.log('remove result', result)
  //       )
  //     : Promise.resolve(filename);
  // })
  // .then((result) => {
  //   return filename;
  // })
  // .catch((err) => {
  //   console.log(err);
  //   return false;
  // });
};

// export const copyContentFilesInBucket = (array, activityId) => {
//   const path = `${activitiesKey}/${activityId}/gamedata/`;

//   const params = array.map(set => ({
//     src: { key: path + set.src, uploadBucket: true },
//     dest: { key: path + set.dest, uploadBucket: true }
//   }));

//   return API.storage.copyFiles(params);
// };

export const changeGameData = (propPath, val) =>
    changeActivityData(`gameData.${propPath}`, val);

export const changeLanguageVersionData = (propPath, val) =>
  changeActivityData(`languageVersionData.${propPath}`, val);

export const changeOptions = (propPath, val) =>
  changeActivityData(`options.${propPath}`, val);

// export const changeHelpHtml = val => changeActivityData('helpHtml', val);

// export const changeHelpAudio = val => changeActivityData('helpAudio', val);

export const changeActivityImage = (val) =>
  changeActivityData('activityImage', val);

export const changeActivityData = (propPath, val) => (dispatch, getState) => {
  const hasUnsavedChanges = getState().flinkMake.editList.hasUnsavedChanges;

  !hasUnsavedChanges && dispatch(setActivitiesUnsavedChanges(true));

  dispatch({
    filter,
    type: CHANGE_ACTIVITY_DATA,
    payload: { propPath, val },
  });
};

export const removeContentFiles = (payload) => {
  return {
    filter,
    type: ADD_CONTENT_FILES_TO_DELETE,
    payload,
  };
};

export const restoreContentFiles = (payload) => {
  return {
    filter,
    type: RESTORE_CONTENT_FILES,
    payload,
  };
};

export const setDeletedProblems = (payload) => ({
  filter,
  type: SET_DELETED_PROBLEMS,
  payload,
});
